/**
 * Suggestion Utility for v2v2 SWFL Farmers Market Application
 * Generates, prioritizes, and groups search suggestions
 */

import { 
  SearchSuggestion, 
  SuggestionGroup, 
  SuggestionType,
  SUGGESTION_LIMITS,
  SUGGESTION_ICONS,
  SUGGESTION_GROUP_TITLES
} from '@/types/suggestions';
import { getSearchIndex } from '@/lib/search-index';
import { 
  getSeasonalData, 
  getCurrentMonth, 
  getPeakProduce, 
  isInSeason,
  getAllProduce 
  } from '@/lib/seasonal-data';

// Load taxonomy categories - simplified for CRA (no dynamic imports)
let taxonomyCategories: Array<{ id: string; name: string; slug: string; description?: string }> = [];

// Default categories for SWFL farmers market directory
const defaultCategories = [
  { id: 'vegetables', name: 'Vegetables', slug: 'vegetables', description: 'Fresh vegetables' },
  { id: 'fruits', name: 'Fruits', slug: 'fruits', description: 'Fresh fruits' },
  { id: 'dairy', name: 'Dairy', slug: 'dairy', description: 'Dairy products' },
  { id: 'meat', name: 'Meat & Poultry', slug: 'meat', description: 'Fresh meat and poultry' },
  { id: 'seafood', name: 'Seafood', slug: 'seafood', description: 'Fresh seafood' },
  { id: 'eggs', name: 'Eggs', slug: 'eggs', description: 'Farm fresh eggs' },
  { id: 'honey', name: 'Honey & Bee Products', slug: 'honey', description: 'Local honey and bee products' },
  { id: 'herbs', name: 'Herbs & Spices', slug: 'herbs', description: 'Fresh herbs and spices' },
  { id: 'baked', name: 'Baked Goods', slug: 'baked', description: 'Fresh baked items' },
  { id: 'preserves', name: 'Preserves & Jams', slug: 'preserves', description: 'Homemade preserves and jams' },
  { id: 'organic', name: 'Organic', slug: 'organic', description: 'Certified organic products' },
  { id: 'local', name: 'Local', slug: 'local', description: 'Locally sourced products' },
];

async function loadTaxonomyCategories(): Promise<Array<{ id: string; name: string; slug: string; description?: string }>> {
  if (taxonomyCategories.length > 0) return taxonomyCategories;
  taxonomyCategories = defaultCategories;
  return taxonomyCategories;
}

// Normalize text for comparison
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Calculate relevance score
function calculateRelevance(query: string, text: string): number {
  const normalizedQuery = normalizeText(query);
  const normalizedText = normalizeText(text);
  
  if (!normalizedQuery || !normalizedText) return 0;
  
  const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0);
  let score = 0;
  
  for (const word of queryWords) {
    // Exact match at start
    if (normalizedText.startsWith(word)) {
      score += 100;
    }
    // Word match early in text
    const index = normalizedText.indexOf(word);
    if (index >= 0 && index < 50) {
      score += 50;
    }
    // Word appears anywhere
    if (normalizedText.includes(word)) {
      score += 10;
    }
  }
  
  return score;
}

// Find highlight ranges for matching text
function findHighlightRanges(query: string, text: string): [number, number][] {
  const ranges: [number, number][] = [];
  const normalizedQuery = normalizeText(query);
  const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 1);
  
  let searchStart = 0;
  for (const word of queryWords) {
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf(word, searchStart);
    if (index >= 0) {
      ranges.push([index, index + word.length]);
      searchStart = index + word.length;
    }
  }
  
  return ranges;
}

// Generate product suggestions from search index
async function getProductSuggestions(query: string): Promise<SearchSuggestion[]> {
  const index = getSearchIndex();
  if (index.length === 0) return [];
  
  const products = index.filter(entry => entry.type === 'product');
  const suggestions: SearchSuggestion[] = [];
  
  for (const product of products) {
    const relevance = calculateRelevance(query, product.name);
    if (relevance > 0) {
      suggestions.push({
        id: product.id,
        text: product.name,
        type: 'product',
        relevance,
        category: product.category,
        icon: SUGGESTION_ICONS.product,
        highlightRanges: findHighlightRanges(query, product.name),
        seasonalNote: product.seasonalMonths?.length 
          ? `Available: ${product.seasonalMonths.slice(0, 3).join(', ')}`
          : undefined,
      });
    }
  }
  
  return suggestions
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, SUGGESTION_LIMITS.products);
}

// Generate category suggestions from taxonomy
async function getCategorySuggestions(query: string): Promise<SearchSuggestion[]> {
  const categories = await loadTaxonomyCategories();
  const suggestions: SearchSuggestion[] = [];
  
  for (const category of categories) {
    const relevance = calculateRelevance(query, category.name);
    if (relevance > 0) {
      suggestions.push({
        id: category.id,
        text: category.name,
        type: 'category',
        relevance,
        icon: SUGGESTION_ICONS.category,
        highlightRanges: findHighlightRanges(query, category.name),
      });
    }
  }
  
  return suggestions
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, SUGGESTION_LIMITS.categories);
}

// Generate vendor suggestions from search index
async function getVendorSuggestions(query: string): Promise<SearchSuggestion[]> {
  const index = getSearchIndex();
  if (index.length === 0) return [];
  
  const vendors = index.filter(entry => entry.type === 'vendor');
  const suggestions: SearchSuggestion[] = [];
  
  for (const vendor of vendors) {
    const relevance = calculateRelevance(query, vendor.name);
    if (relevance > 0) {
      suggestions.push({
        id: vendor.id,
        text: vendor.name,
        type: 'vendor',
        relevance,
        category: vendor.category,
        icon: SUGGESTION_ICONS.vendor,
        highlightRanges: findHighlightRanges(query, vendor.name),
      });
    }
  }
  
  return suggestions
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, SUGGESTION_LIMITS.vendors);
}

// Generate market suggestions from search index
async function getMarketSuggestions(query: string): Promise<SearchSuggestion[]> {
  const index = getSearchIndex();
  if (index.length === 0) return [];
  
  const markets = index.filter(entry => entry.type === 'market');
  const suggestions: SearchSuggestion[] = [];
  
  for (const market of markets) {
    const relevance = calculateRelevance(query, market.name);
    if (relevance > 0) {
      suggestions.push({
        id: market.id,
        text: market.name,
        type: 'market',
        relevance,
        location: market.location?.city,
        icon: SUGGESTION_ICONS.market,
        highlightRanges: findHighlightRanges(query, market.name),
      });
    }
  }
  
  return suggestions
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, SUGGESTION_LIMITS.markets);
}

// Get recent searches from localStorage
export function getRecentSearches(): string[] {
  if (typeof window === 'undefined') return [];
  
  try {
    const stored = localStorage.getItem('v2v2_recent_searches');
    if (stored) {
      const searches = JSON.parse(stored);
      return Array.isArray(searches) ? searches : [];
    }
  } catch (error) {
    console.error('Error reading recent searches:', error);
  }
  
  return [];
}

// Save recent search to localStorage
export function saveRecentSearch(query: string): void {
  if (typeof window === 'undefined' || !query.trim()) return;
  
  try {
    const searches = getRecentSearches();
    const normalizedQuery = query.trim().toLowerCase();
    
    // Remove if exists (to move to top)
    const filtered = searches.filter(s => s.toLowerCase() !== normalizedQuery);
    
    // Add to front
    filtered.unshift(query.trim());
    
    // Limit to 10
    const limited = filtered.slice(0, 10);
    
    localStorage.setItem('v2v2_recent_searches', JSON.stringify(limited));
  } catch (error) {
    console.error('Error saving recent search:', error);
  }
}

// Clear all recent searches
export function clearRecentSearches(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem('v2v2_recent_searches');
  } catch (error) {
    console.error('Error clearing recent searches:', error);
  }
}

// Get recent searches as suggestions
function getRecentSuggestions(query: string): SearchSuggestion[] {
  const recent = getRecentSearches();
  
  if (!query.trim()) {
    // Show all recent searches if no query
    return recent.slice(0, SUGGESTION_LIMITS.recent).map((text, index) => ({
      id: `recent-${index}`,
      text,
      type: 'recent' as SuggestionType,
      relevance: 10 - index,
      icon: SUGGESTION_ICONS.recent,
    }));
  }
  
  // Filter by query
  const normalizedQuery = normalizeText(query);
  const filtered = recent.filter(search => 
    normalizeText(search).includes(normalizedQuery)
  );
  
  return filtered.slice(0, SUGGESTION_LIMITS.recent).map((text, index) => ({
    id: `recent-${index}`,
    text,
    type: 'recent' as SuggestionType,
    relevance: 10 - index,
    icon: SUGGESTION_ICONS.recent,
    highlightRanges: findHighlightRanges(query, text),
  }));
}

// Generate seasonal suggestions (in-season produce)
async function getSeasonalSuggestions(query: string): Promise<SearchSuggestion[]> {
  try {
    const seasonalData = await getSeasonalData();
    const currentMonth = getCurrentMonth();
    const peakProduce = getPeakProduce(seasonalData);
    const allProduce = getAllProduce(seasonalData);
    
    // Get produce that's currently in season
    const inSeasonProduce = allProduce.filter(p => 
      isInSeason(p.name, seasonalData)
    );
    
    const suggestions: SearchSuggestion[] = [];
    
    for (const produce of inSeasonProduce) {
      const relevance = calculateRelevance(query, produce.name);
      if (relevance > 0 || !query.trim()) {
        const status = peakProduce.includes(produce.name) ? 'Peak Season!' : 'In Season';
        
        suggestions.push({
          id: `seasonal-${produce.name.toLowerCase().replace(/\s+/g, '-')}`,
          text: produce.name,
          type: 'seasonal',
          relevance: relevance + (peakProduce.includes(produce.name) ? 50 : 0),
          category: produce.season,
          icon: SUGGESTION_ICONS.seasonal,
          seasonalNote: status,
          highlightRanges: query.trim() ? findHighlightRanges(query, produce.name) : undefined,
        });
      }
    }
    
    // If no query, prioritize peak produce
    if (!query.trim()) {
      suggestions.sort((a, b) => {
        // Peak season items first
        const aPeak = a.seasonalNote === 'Peak Season!';
        const bPeak = b.seasonalNote === 'Peak Season!';
        if (aPeak && !bPeak) return -1;
        if (!aPeak && bPeak) return 1;
        return b.relevance - a.relevance;
      });
    }
    
    return suggestions.slice(0, SUGGESTION_LIMITS.seasonal);
  } catch (error) {
    console.error('Error getting seasonal suggestions:', error);
    return [];
  }
}

// Main function to get all suggestions grouped
export async function getSuggestionsForQuery(query: string): Promise<SuggestionGroup[]> {
  if (!query.trim() && typeof window !== 'undefined') {
    // No query - show recent searches and seasonal suggestions
    const recentGroup: SuggestionGroup = {
      title: SUGGESTION_GROUP_TITLES.recent,
      icon: SUGGESTION_ICONS.recent,
      suggestions: getRecentSuggestions(query),
    };
    
    const seasonalGroup: SuggestionGroup = {
      title: SUGGESTION_GROUP_TITLES.seasonal,
      icon: SUGGESTION_ICONS.seasonal,
      suggestions: await getSeasonalSuggestions(query),
    };
    
    return [recentGroup, seasonalGroup].filter(g => g.suggestions.length > 0);
  }
  
  // Query provided - get matching suggestions
  const [
    productSuggestions,
    categorySuggestions,
    vendorSuggestions,
    marketSuggestions,
    recentSuggestions,
    seasonalSuggestions,
  ] = await Promise.all([
    getProductSuggestions(query),
    getCategorySuggestions(query),
    getVendorSuggestions(query),
    getMarketSuggestions(query),
    Promise.resolve(getRecentSuggestions(query)),
    getSeasonalSuggestions(query),
  ]);
  
  const groups: SuggestionGroup[] = [];
  
  if (productSuggestions.length > 0) {
    groups.push({
      title: SUGGESTION_GROUP_TITLES.product,
      icon: SUGGESTION_ICONS.product,
      suggestions: productSuggestions,
    });
  }
  
  if (categorySuggestions.length > 0) {
    groups.push({
      title: SUGGESTION_GROUP_TITLES.category,
      icon: SUGGESTION_ICONS.category,
      suggestions: categorySuggestions,
    });
  }
  
  if (seasonalSuggestions.length > 0) {
    groups.push({
      title: SUGGESTION_GROUP_TITLES.seasonal,
      icon: SUGGESTION_ICONS.seasonal,
      suggestions: seasonalSuggestions,
    });
  }
  
  if (vendorSuggestions.length > 0) {
    groups.push({
      title: SUGGESTION_GROUP_TITLES.vendor,
      icon: SUGGESTION_ICONS.vendor,
      suggestions: vendorSuggestions,
    });
  }
  
  if (marketSuggestions.length > 0) {
    groups.push({
      title: SUGGESTION_GROUP_TITLES.market,
      icon: SUGGESTION_ICONS.market,
      suggestions: marketSuggestions,
    });
  }
  
  if (recentSuggestions.length > 0) {
    groups.push({
      title: SUGGESTION_GROUP_TITLES.recent,
      icon: SUGGESTION_ICONS.recent,
      suggestions: recentSuggestions,
    });
  }
  
  return groups;
}

// Flatten all suggestions for keyboard navigation
export function flattenSuggestions(groups: SuggestionGroup[]): SearchSuggestion[] {
  return groups.flatMap(group => group.suggestions);
}

// Get total count of all suggestions
export function getTotalSuggestionCount(groups: SuggestionGroup[]): number {
  return groups.reduce((total, group) => total + group.suggestions.length, 0);
}
